import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ConsentBanner, OPEN_CONSENT_EVENT } from "@/components/ConsentBanner";
import { AuthProvider } from "@/context/AuthContext";
import { StarfieldBackground } from "@/components/StarfieldBackground";
import { Navbar } from "@/components/Navbar";
import { AuthCallback } from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Journal from "@/pages/Journal";
import DailyRitual from "@/pages/DailyRitual";
import Library from "@/pages/Library";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import SubscriptionTerms from "@/pages/SubscriptionTerms";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import { SignInDialog } from "@/components/SignInDialog";

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  // Process OAuth return synchronously during render (before any auth check).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Navbar />
      <main className="relative">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/daily" element={<DailyRitual />} />
          <Route path="/library" element={<Library />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/terms" element={<SubscriptionTerms />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/auth/verify" element={<VerifyEmail />} />
          <Route path="/auth/reset" element={<ResetPassword />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <footer className="relative mx-auto flex max-w-6xl flex-wrap justify-center gap-x-5 gap-y-2 px-5 py-8 text-center text-xs text-[#A3A5B8] sm:px-8" data-testid="site-footer">
        <Link to="/pricing" data-testid="footer-pricing-link" className="underline underline-offset-4 hover:text-[#F8F3E6]">Pricing</Link>
        <Link to="/about" data-testid="footer-about-link" className="underline underline-offset-4 hover:text-[#F8F3E6]">About</Link>
        <Link to="/#faq" data-testid="footer-faq-link" className="underline underline-offset-4 hover:text-[#F8F3E6]">FAQ</Link>
        <Link to="/library" data-testid="footer-library-link" className="underline underline-offset-4 hover:text-[#F8F3E6]">Card library</Link>
        <Link to="/privacy" data-testid="footer-privacy-link" className="underline underline-offset-4 hover:text-[#F8F3E6]">Privacy Policy</Link>
        <button type="button" onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))} data-testid="footer-analytics-choices" className="underline underline-offset-4 hover:text-[#F8F3E6]">Analytics choices</button>
        <Link to="/terms" data-testid="footer-terms-link" className="underline underline-offset-4 hover:text-[#F8F3E6]">
          Subscription terms &amp; refunds
        </Link>
      </footer>
    </>
  );
}

function App() {
  return (
    <div className="App dark min-h-screen">
      <StarfieldBackground />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <SignInDialog />
          <ConsentBanner />
          <Toaster
            position="top-center"
            offset={{ top: window.innerWidth >= 1024 ? 88 : 132 }}
            mobileOffset={{ top: 124, left: 12, right: 12 }}
            toastOptions={{
              style: {
                background: "#131422",
                border: "1px solid rgba(217,164,65,0.3)",
                color: "#F8F3E6",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
